import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import {
  getPiercingRecord,
  upsertPiercingRecord,
  UpsertPiercingRecordData,
  PiercingType,
} from '../services/piercingRecordApi';
import {
  getTattooRecord,
  upsertTattooRecord,
  UpsertTattooRecordData,
  TattooStyle,
  ColorType,
  ComplexityLevel,
} from '../services/tattooRecordApi';

// Helper function to safely format datetime for datetime-local input
const formatDateTimeForInput = (dateValue: any): string => {
  if (!dateValue) return '';

  try {
    let date: Date;

    if (typeof dateValue === 'string') {
      if (!dateValue.trim()) return '';
      date = new Date(dateValue);
    } else if (typeof dateValue === 'object' && dateValue !== null) {
      return '';
    } else {
      date = new Date(dateValue);
    }

    // Check if the date is valid
    if (isNaN(date.getTime())) {
      console.warn('Invalid datetime value:', dateValue);
      return '';
    }

    // Check for default/placeholder dates (before year 1950)
    if (date.getFullYear() < 1950) {
      return '';
    }

    // datetime-local expects local time (YYYY-MM-DDTHH:mm), not UTC.
    // Using toISOString() here shifts displayed hour in non-UTC timezones.
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  } catch (error) {
    console.warn('Error formatting datetime:', dateValue, error);
    return '';
  }
};
import { ArrowLeft, Save, Calendar, Clock, FileText, AlertTriangle } from 'lucide-react';
import { HelpIcon } from '../components/HelpIcon';
import { apiClient } from '../utils/api';
import {
  getClientNoteSections,
  isMinor,
  type ClientNoteSection,
  type SupportedPhoneCountry,
} from '@digitaltattoo/shared-utils';
import { useCurrentShop } from '../contexts/CurrentShopContext';
import { ALL_SHOPS_ID } from '../utils/currentShop';
import { InternationalPhoneInput } from '../components/InternationalPhoneInput';
import { DateTimeInput } from '../components/DateTimeInput';
import { DateInput } from '../components/DateInput';
import { localizeAppointmentApiMessage } from '../utils/localizeApiMessage';

interface AppointmentFormData {
  id: string;
  clientId: string;
  practitionerId: string; // empty string means "unassigned"
  reservedPractitionerId?: string | null;
  shopId: string;
  serviceId: string;
  scheduledAt: string;
  estimatedEndTime: string;
  actualStartTime?: string;
  actualEndTime?: string;
  status: string;
  type: string;
  clientIsMinor?: boolean | null;
  legalGuardian?: {
    firstName?: string | null;
    lastName?: string | null;
    email?: string | null;
    phone?: string | null;
  } | null;
  notes: string;
  clientNotes?: string;
  pricing: {
    basePrice: number;
    supplements?: Array<{ name: string; price: number }>;
    subtotal: number;
    deposit: number;
    total: number;
    paymentMethod: string;
    currency: string;
  };
  preparation: {
    beforeAppointment: string[];
    whatToBring: string[];
    restrictions: string[];
  };
  reminders: {
    enabled: boolean;
    methods: string[];
    daysBefore: number[];
    customMessage?: string;
  };
  createdAt: string;
  updatedAt: string;
}

interface SelectOption {
  id: string;
  name: string;
  price?: number;
  estimatedDuration?: number;
  planningRecommendation?: string | null;
  category?: string;
}

interface BodyZoneOption {
  id: string;
  name: string;
}

interface PiercingTypeOption {
  id: string;
  name: string;
  bodyZoneId: string;
  basePrice?: number;
  estimatedDuration?: number;
}

interface RequestedPiercingItem {
  bodyZoneId: string;
  piercingTypeId: string;
}

interface QuickClientFormData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  dateOfBirth: string;
  isMinor: boolean | null;
  consentGiven: boolean;
}

interface ShopPiercingOffering {
  id: string;
  customPrice?: number | null;
  customDurationMinutes?: number | null;
  customMinorDurationMinutes?: number | null;
  basePrice?: number | null;
  estimatedDuration?: number | null;
  isOffered?: boolean;
  isAvailable?: boolean;
}

interface AppointmentAct {
  id: string;
  serviceId: string;
  serviceCategory?: 'PIERCING' | 'TATTOO' | 'CONSULTATION' | 'REMOVAL' | 'AFTERCARE' | null;
  practitionerId?: string | null;
  shopId: string;
  clientId?: string | null;
  sequence: number;
  durationMinutes: number;
  plannedStartAt?: string | null;
  plannedEndAt?: string | null;
  demandDetails?: Record<string, any> | null;
  status: 'PLANNED' | 'IN_PROGRESS' | 'COMPLETED' | 'CANCELLED';
  notes?: string | null;
}

/** Catégorie métier effective (les actes créés en prise en charge peuvent avoir `serviceCategory` null). */
function effectiveActCategory(
  act: AppointmentAct,
  services: SelectOption[]
): AppointmentAct['serviceCategory'] {
  if (act.serviceCategory) return act.serviceCategory;
  const cat = services.find((s) => s.id === act.serviceId)?.category;
  return (cat as AppointmentAct['serviceCategory']) ?? null;
}

const getAutoTypeLabelFromCategory = (category?: string): string | null => {
  if (!category) return null;
  if (category === 'PIERCING') return 'Piercing';
  if (category === 'TATTOO') return 'Seance tatouage';
  if (category === 'CONSULTATION') return 'Consultation';
  if (category === 'REMOVAL') return 'Detatouage';
  if (category === 'AFTERCARE') return 'Suivi';
  return null;
};

const roundPrice = (value: number): number => Math.round(value * 100) / 100;

const byLocalizedName = <T extends { name: string }>(left: T, right: T): number =>
  left.name.localeCompare(right.name, 'fr', { sensitivity: 'base', numeric: true });

const extractPiercingsFromNotes = (
  notes?: string | null
): Array<{ bodyZone?: string; name?: string }> => {
  if (!notes) return [];
  const match = notes.match(/Piercings:\s*([^.]*)/i);
  if (!match?.[1]) return [];
  return match[1]
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean)
    .map((entry) => {
      const [bodyZoneRaw, nameRaw] = entry.split('-').map((s) => s.trim());
      if (!bodyZoneRaw && !nameRaw) return {};
      if (!nameRaw) return { name: bodyZoneRaw };
      return { bodyZone: bodyZoneRaw, name: nameRaw };
    });
};

const dateTimeLocalToIso = (value?: string | null): string | null => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toISOString();
};

export function AppointmentEditPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { currentShopId, setCurrentShopId } = useCurrentShop();

  const [appointment, setAppointment] = useState<AppointmentFormData | null>(null);
  const [clients, setClients] = useState<SelectOption[]>([]);
  const [practitioners, setPractitioners] = useState<SelectOption[]>([]);
  const [shops, setShops] = useState<SelectOption[]>([]);
  const [services, setServices] = useState<SelectOption[]>([]);
  const [bodyZones, setBodyZones] = useState<BodyZoneOption[]>([]);
  const [piercingTypes, setPiercingTypes] = useState<PiercingTypeOption[]>([]);
  const [shopPiercingOfferings, setShopPiercingOfferings] = useState<
    Record<string, ShopPiercingOffering>
  >({});
  const [requestedPiercings, setRequestedPiercings] = useState<RequestedPiercingItem[]>([
    { bodyZoneId: '', piercingTypeId: '' },
  ]);
  const [piercingInitialized, setPiercingInitialized] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errorDetails, setErrorDetails] = useState<string[]>([]);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showCreateClientModal, setShowCreateClientModal] = useState(false);
  const [creatingClient, setCreatingClient] = useState(false);
  const [createClientError, setCreateClientError] = useState<string | null>(null);
  const [appointmentActs, setAppointmentActs] = useState<AppointmentAct[]>([]);
  const [actsLoading, setActsLoading] = useState(false);
  const [actsSaving, setActsSaving] = useState(false);
  const [actsError, setActsError] = useState<string | null>(null);

  type PiercingRecordState = {
    form: UpsertPiercingRecordData;
    complicationsText: string;
    exists: boolean;
  };
  const defaultPiercingForm = (): UpsertPiercingRecordData => ({
    placement: { area: '', side: undefined, specific: '' },
    jewelry: { type: '', material: '', gauge: '' },
    technique: { method: '', tools: '' },
    aftercareProvided: { products: '', instructions: '' },
    complications: [],
    isHealed: false,
  });
  const [piercingRecords, setPiercingRecords] = useState<Record<string, PiercingRecordState>>({});
  const piercingRecordsRef = useRef(piercingRecords);
  piercingRecordsRef.current = piercingRecords;
  const [piercingModalActId, setPiercingModalActId] = useState<string | null>(null);
  const [piercingModalSaving, setPiercingModalSaving] = useState(false);
  const [piercingModalError, setPiercingModalError] = useState<string | null>(null);

  type TattooRecordState = {
    form: UpsertTattooRecordData;
    exists: boolean;
  };
  const defaultTattooForm = (): UpsertTattooRecordData => ({
    style: undefined,
    colorType: undefined,
    complexity: undefined,
    size: {},
    placement: {},
    designImages: [],
    sessionNotes: '',
    isCompleted: false,
  });
  const [tattooRecords, setTattooRecords] = useState<Record<string, TattooRecordState>>({});
  const tattooRecordsRef = useRef(tattooRecords);
  tattooRecordsRef.current = tattooRecords;
  const [tattooModalActId, setTattooModalActId] = useState<string | null>(null);
  const [tattooModalSaving, setTattooModalSaving] = useState(false);
  const [tattooModalError, setTattooModalError] = useState<string | null>(null);

  const [quickClientData, setQuickClientData] = useState<QuickClientFormData>({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    dateOfBirth: '',
    isMinor: null,
    consentGiven: false,
  });
  const [quickClientPhoneCountry, setQuickClientPhoneCountry] =
    useState<SupportedPhoneCountry>('FR');
  const [quickClientPhoneDisplay, setQuickClientPhoneDisplay] = useState('');
  const [quickClientPhoneE164, setQuickClientPhoneE164] = useState<string | null>(null);
  const [quickClientPhoneIsValid, setQuickClientPhoneIsValid] = useState(false);
  const clientNoteSections = appointment ? getClientNoteSections(appointment.clientNotes) : [];

  useEffect(() => {
    if (id) {
      fetchAppointment(id);
      fetchAppointmentActs(id);
      fetchSelectOptions();
    }
  }, [id]);

  // Keep form shop in sync with the global CurrentShopSelector
  useEffect(() => {
    if (!currentShopId || currentShopId === ALL_SHOPS_ID) return;
    if (!appointment || appointment.shopId === currentShopId) return;
    setAppointment((prev) =>
      prev
        ? {
            ...prev,
            shopId: currentShopId,
            serviceId: '',
            practitionerId: '',
          }
        : prev
    );
    setHasUnsavedChanges(true);
  }, [currentShopId, appointment?.shopId]);

  useEffect(() => {
    fetchServicesForShop(appointment?.shopId || undefined);
  }, [appointment?.shopId]);

  useEffect(() => {
    fetchPractitionersForSelection(appointment?.shopId || '', appointment?.serviceId || '');
  }, [appointment?.shopId, appointment?.serviceId]);

  useEffect(() => {
    fetchShopPiercingOfferings(appointment?.shopId || undefined);
  }, [appointment?.shopId]);

  const resolveDefaultShopId = (shopOptions: SelectOption[]): string => {
    if (!shopOptions.length) return '';
    // In "all shops" mode (or no selection), leave unselected so all shops remain choosable
    if (
      currentShopId &&
      currentShopId !== ALL_SHOPS_ID &&
      shopOptions.some((shop) => shop.id === currentShopId)
    ) {
      return currentShopId;
    }
    return shopOptions.length === 1 ? shopOptions[0].id : '';
  };

  useEffect(() => {
    if (!appointment || piercingInitialized) return;
    const pricing = appointment.pricing as any;
    const pricingPiercings = Array.isArray(pricing?.piercingTypes)
      ? pricing.piercingTypes
      : pricing?.piercingType
        ? [pricing.piercingType]
        : [];
    const fallbackFromNotes = extractPiercingsFromNotes(appointment.notes);
    const sourcePiercings = pricingPiercings.length > 0 ? pricingPiercings : fallbackFromNotes;
    if (sourcePiercings.length === 0) {
      setPiercingInitialized(true);
      return;
    }
    const mapped = sourcePiercings
      .map((item: any) => {
        const typeId = item?.id || item?.piercingTypeId || '';
        // Fast path: pricing entry already carries bodyZoneId (new bookings)
        if (typeId && item?.bodyZoneId) {
          return { bodyZoneId: item.bodyZoneId, piercingTypeId: typeId };
        }
        if (typeId) {
          const matchedType = piercingTypes.find((pt) => pt.id === typeId);
          if (matchedType) {
            return { bodyZoneId: matchedType.bodyZoneId, piercingTypeId: matchedType.id };
          }
        }
        const byName = piercingTypes.find(
          (pt) => pt.name === item?.name || pt.name === item?.piercingType
        );
        if (byName) {
          return { bodyZoneId: byName.bodyZoneId, piercingTypeId: byName.id };
        }
        const zoneByName = bodyZones.find(
          (z) => z.name === item?.bodyZone || z.name === item?.zone
        );
        if (zoneByName) {
          const inZone = piercingTypes.find(
            (pt) =>
              pt.bodyZoneId === zoneByName.id &&
              (pt.name === item?.name || pt.name === item?.piercingType)
          );
          if (inZone) {
            return { bodyZoneId: zoneByName.id, piercingTypeId: inZone.id };
          }
        }
        return null;
      })
      .filter(Boolean) as RequestedPiercingItem[];
    if (mapped.length > 0) {
      setRequestedPiercings(mapped);
      setAppointment((prev) => (prev ? { ...prev, type: 'PIERCING' } : prev));
      setPiercingInitialized(true);
    } else if (piercingTypes.length > 0) {
      // piercingTypes is loaded but nothing matched — finalize anyway
      setPiercingInitialized(true);
    }
    // If piercingTypes not yet loaded, don't finalize — the effect will retry when they arrive
  }, [appointment, piercingTypes, bodyZones, piercingInitialized]);

  useEffect(() => {
    if (!appointment) return;
    const selectedService = services.find((s) => s.id === appointment.serviceId);
    if (!selectedService) return;

    const controller = new AbortController();
    let fallbackDurationMinutes = Math.max(1, Number(selectedService.estimatedDuration || 60));
    let basePrice = Number(selectedService.price || 0);
    let selectedPiercingTypeIds: string[] = [];

    if (appointment.type === 'PIERCING') {
      selectedPiercingTypeIds = requestedPiercings
        .filter((item) => item.bodyZoneId && item.piercingTypeId)
        .map((item) => item.piercingTypeId);
      if (selectedPiercingTypeIds.length > 0) {
        fallbackDurationMinutes = 0;
        basePrice = 0;
        for (const piercingTypeId of selectedPiercingTypeIds) {
          const typeDef = piercingTypes.find((pt) => pt.id === piercingTypeId);
          const offering = shopPiercingOfferings[piercingTypeId];
          fallbackDurationMinutes += Math.max(
            1,
            Number(
              offering?.customDurationMinutes ??
                selectedService.estimatedDuration ??
                30
            )
          );
          const piercingPrice = Number(
            offering?.customPrice ?? offering?.basePrice ?? typeDef?.basePrice ?? 0
          );
          basePrice += piercingPrice;
        }
      }
    }

    const nextBasePrice = roundPrice(basePrice);
    const nextDeposit = roundPrice(nextBasePrice * 0.5);
    const scheduledDate = appointment.scheduledAt ? new Date(appointment.scheduledAt) : null;
    const fallbackEndIso =
      scheduledDate && !Number.isNaN(scheduledDate.getTime())
        ? new Date(scheduledDate.getTime() + fallbackDurationMinutes * 60 * 1000).toISOString()
        : appointment.estimatedEndTime;

    setAppointment((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        estimatedEndTime: appointment.type === 'PIERCING'
          ? prev.estimatedEndTime
          : fallbackEndIso,
        pricing: {
          ...(prev.pricing || {}),
          basePrice: nextBasePrice,
          total: nextBasePrice,
          deposit: nextDeposit,
        },
      };
    });

    if (
      appointment.type === 'PIERCING'
      && appointment.shopId
      && appointment.clientId
      && selectedPiercingTypeIds.length > 0
    ) {
      const query = new URLSearchParams({
        shopId: appointment.shopId,
        serviceId: appointment.serviceId,
        clientId: appointment.clientId,
        actCount: String(selectedPiercingTypeIds.length),
        piercingTypeIds: selectedPiercingTypeIds.join(','),
      });
      apiClient(`/api/admin/appointments/duration-preview?${query.toString()}`, {
        signal: controller.signal,
      })
        .then((response) => response.json())
        .then((data) => {
          if (!data?.success || !scheduledDate || Number.isNaN(scheduledDate.getTime())) return;
          const durationMinutes = Math.max(1, Number(data.data?.durationMinutes || fallbackDurationMinutes));
          const estimatedEndTime = new Date(
            scheduledDate.getTime() + durationMinutes * 60 * 1000,
          ).toISOString();
          setAppointment((prev) => prev ? { ...prev, estimatedEndTime } : prev);
        })
        .catch((previewError) => {
          if (previewError instanceof DOMException && previewError.name === 'AbortError') return;
          console.error('Fetch appointment duration preview error:', previewError);
          setAppointment((prev) => prev ? { ...prev, estimatedEndTime: fallbackEndIso } : prev);
        });
    }

    return () => controller.abort();
  }, [
    appointment?.serviceId,
    appointment?.type,
    appointment?.shopId,
    appointment?.clientId,
    appointment?.scheduledAt,
    services,
    requestedPiercings,
    piercingTypes,
    shopPiercingOfferings,
  ]);

  const fetchAppointment = async (appointmentId: string) => {
    try {
      setLoading(true);
      setError(null);
      setErrorDetails([]);

      const response = await apiClient(`/api/admin/appointments/${appointmentId}`);

      const data = await response.json();
      if (data.success) {
        console.log('📊 Raw appointment data:', data.data.appointment);

        // Ensure appointment has valid default datetime values if they're missing
        const appointmentData = data.data.appointment;
        if (appointmentData.shopId && appointmentData.shopId !== currentShopId) {
          // An appointment opened from the all-shops list must be edited in its own shop scope.
          setCurrentShopId(appointmentData.shopId);
        }
        const now = new Date();
        const tomorrow = new Date(now.getTime() + 24 * 60 * 60 * 1000);
        const tomorrowPlusHour = new Date(tomorrow.getTime() + 60 * 60 * 1000);

        const appointmentWithDefaults = {
          ...appointmentData,
          scheduledAt: appointmentData.scheduledAt || tomorrow.toISOString(),
          estimatedEndTime: appointmentData.estimatedEndTime || tomorrowPlusHour.toISOString(),
          practitionerId: appointmentData.practitionerId || '',
          type:
            Array.isArray((appointmentData as any)?.pricing?.piercingTypes) &&
            (appointmentData as any).pricing.piercingTypes.length > 0
              ? 'PIERCING'
              : appointmentData.type,
        };

        console.log('📊 Appointment with defaults:', appointmentWithDefaults);
        setAppointment(appointmentWithDefaults);
      } else {
        setError(
          localizeAppointmentApiMessage(data.message) || 'Échec du chargement du rendez-vous'
        );
        if (Array.isArray(data.errors) && data.errors.length > 0) {
          setErrorDetails(data.errors);
        }
      }
    } catch (error) {
      console.error('Fetch appointment error:', error);
      setError('Erreur de connexion au serveur');
    } finally {
      setLoading(false);
    }
  };

  const fetchAppointmentActs = async (appointmentId: string, retryCount = 0) => {
    try {
      setActsLoading(true);
      setActsError(null);
      const response = await apiClient(`/api/admin/appointments/${appointmentId}/acts`);
      const data = await response.json();
      if (!data?.success) {
        setActsError(data?.message || 'Impossible de charger les actes.');
        return;
      }
      // Acts are created asynchronously after appointment creation.
      // If none found yet, retry up to 3 times with a short delay.
      if ((data.data?.acts || []).length === 0 && retryCount < 3) {
        await new Promise((resolve) => setTimeout(resolve, 1200));
        return fetchAppointmentActs(appointmentId, retryCount + 1);
      }
      const mappedActs: AppointmentAct[] = (data.data?.acts || []).map((act: any) => ({
        id: act.id,
        serviceId: act.serviceId,
        serviceCategory: act.serviceCategory || null,
        practitionerId: act.practitionerId || null,
        shopId: act.shopId,
        clientId: act.clientId || null,
        sequence: Number(act.sequence || 1),
        durationMinutes: Number(act.durationMinutes || 30),
        plannedStartAt: act.plannedStartAt || null,
        plannedEndAt: act.plannedEndAt || null,
        demandDetails: act.demandDetails || null,
        status: (act.status || 'PLANNED') as AppointmentAct['status'],
        notes: act.notes || '',
      }));
      setAppointmentActs(mappedActs);

      // Load piercing records for PIERCING acts (incl. catégorie dérivée du service si l’API renvoie null)
      const piercingActs = mappedActs.filter(
        (a) => effectiveActCategory(a, services) === 'PIERCING'
      );
      if (piercingActs.length > 0) {
        const entries = await Promise.all(
          piercingActs.map(async (act) => {
            try {
              const record = await getPiercingRecord(act.id);
              if (record) {
                // Prefer IDs from the record; fall back to act.demandDetails for legacy data
                const bodyZoneId = record.bodyZoneId ?? act.demandDetails?.bodyZoneId ?? undefined;
                const piercingTypeModelId =
                  record.piercingTypeModelId ?? act.demandDetails?.piercingTypeModelId ?? undefined;
                return [
                  act.id,
                  {
                    form: {
                      bodyZoneId,
                      piercingTypeModelId,
                      piercingType: record.piercingType,
                      placement: record.placement,
                      jewelry: record.jewelry,
                      technique: record.technique,
                      needleGauge: record.needleGauge,
                      healingTime: record.healingTime,
                      aftercareProvided: record.aftercareProvided,
                      complications: record.complications ?? [],
                      isHealed: record.isHealed,
                    } as UpsertPiercingRecordData,
                    complicationsText: (record.complications ?? []).join(', '),
                    exists: true,
                  },
                ] as [string, PiercingRecordState];
              }
            } catch {
              /* non-blocking */
            }
            // No record yet — pre-fill from demandDetails if available
            const defaultForm = defaultPiercingForm();
            defaultForm.bodyZoneId = act.demandDetails?.bodyZoneId ?? undefined;
            defaultForm.piercingTypeModelId = act.demandDetails?.piercingTypeModelId ?? undefined;
            return [act.id, { form: defaultForm, complicationsText: '', exists: false }] as [
              string,
              PiercingRecordState,
            ];
          })
        );
        setPiercingRecords((prev) => ({ ...prev, ...Object.fromEntries(entries) }));
      }

      // Load tattoo records for TATTOO acts
      const tattooActs = mappedActs.filter((a) => effectiveActCategory(a, services) === 'TATTOO');
      if (tattooActs.length > 0) {
        const entries = await Promise.all(
          tattooActs.map(async (act) => {
            try {
              const record = await getTattooRecord(act.id);
              if (record) {
                return [
                  act.id,
                  {
                    form: {
                      bodyZoneId: record.bodyZoneId ?? undefined,
                      style: record.style ?? undefined,
                      colorType: record.colorType ?? undefined,
                      complexity: record.complexity ?? undefined,
                      size: record.size ?? {},
                      placement: record.placement ?? {},
                      designImages: record.designImages ?? [],
                      sessionNotes: record.sessionNotes ?? '',
                      isCompleted: record.isCompleted ?? false,
                    } as UpsertTattooRecordData,
                    exists: true,
                  },
                ] as [string, TattooRecordState];
              }
            } catch {
              /* non-blocking */
            }
            return [act.id, { form: defaultTattooForm(), exists: false }] as [
              string,
              TattooRecordState,
            ];
          })
        );
        setTattooRecords((prev) => ({ ...prev, ...Object.fromEntries(entries) }));
      }
    } catch (fetchError) {
      console.error('Fetch appointment acts error:', fetchError);
      setActsError('Erreur de connexion au serveur (actes).');
    } finally {
      setActsLoading(false);
    }
  };

  /** Quand les services sont chargés après les actes, compléter fiches piercing/tatouage pour actes sans catégorie persistée. */
  useEffect(() => {
    if (!services.length || appointmentActs.length === 0) return;

    const piercingActs = appointmentActs.filter(
      (a) => effectiveActCategory(a, services) === 'PIERCING'
    );
    const missingPiercing = piercingActs.filter(
      (a) => piercingRecordsRef.current[a.id] === undefined
    );
    const tattooActs = appointmentActs.filter(
      (a) => effectiveActCategory(a, services) === 'TATTOO'
    );
    const missingTattoo = tattooActs.filter((a) => tattooRecordsRef.current[a.id] === undefined);
    if (missingPiercing.length === 0 && missingTattoo.length === 0) return;

    let cancelled = false;
    (async () => {
      if (missingPiercing.length > 0) {
        const entries = await Promise.all(
          missingPiercing.map(async (act) => {
            try {
              const record = await getPiercingRecord(act.id);
              if (record) {
                const bodyZoneId = record.bodyZoneId ?? act.demandDetails?.bodyZoneId ?? undefined;
                const piercingTypeModelId =
                  record.piercingTypeModelId ?? act.demandDetails?.piercingTypeModelId ?? undefined;
                return [
                  act.id,
                  {
                    form: {
                      bodyZoneId,
                      piercingTypeModelId,
                      piercingType: record.piercingType,
                      placement: record.placement,
                      jewelry: record.jewelry,
                      technique: record.technique,
                      needleGauge: record.needleGauge,
                      healingTime: record.healingTime,
                      aftercareProvided: record.aftercareProvided,
                      complications: record.complications ?? [],
                      isHealed: record.isHealed,
                    } as UpsertPiercingRecordData,
                    complicationsText: (record.complications ?? []).join(', '),
                    exists: true,
                  },
                ] as [string, PiercingRecordState];
              }
            } catch {
              /* non-blocking */
            }
            const defaultForm = defaultPiercingForm();
            defaultForm.bodyZoneId = act.demandDetails?.bodyZoneId ?? undefined;
            defaultForm.piercingTypeModelId = act.demandDetails?.piercingTypeModelId ?? undefined;
            return [act.id, { form: defaultForm, complicationsText: '', exists: false }] as [
              string,
              PiercingRecordState,
            ];
          })
        );
        if (!cancelled) {
          setPiercingRecords((prev) => ({ ...prev, ...Object.fromEntries(entries) }));
        }
      }
      if (missingTattoo.length > 0) {
        const tEntries = await Promise.all(
          missingTattoo.map(async (act) => {
            try {
              const record = await getTattooRecord(act.id);
              if (record) {
                return [
                  act.id,
                  {
                    form: {
                      bodyZoneId: record.bodyZoneId ?? undefined,
                      style: record.style ?? undefined,
                      colorType: record.colorType ?? undefined,
                      complexity: record.complexity ?? undefined,
                      size: record.size ?? {},
                      placement: record.placement ?? {},
                      designImages: record.designImages ?? [],
                      sessionNotes: record.sessionNotes ?? '',
                      isCompleted: record.isCompleted ?? false,
                    } as UpsertTattooRecordData,
                    exists: true,
                  },
                ] as [string, TattooRecordState];
              }
            } catch {
              /* non-blocking */
            }
            return [act.id, { form: defaultTattooForm(), exists: false }] as [
              string,
              TattooRecordState,
            ];
          })
        );
        if (!cancelled) {
          setTattooRecords((prev) => ({ ...prev, ...Object.fromEntries(tEntries) }));
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [services, appointmentActs]);

  const fetchSelectOptions = async () => {
    try {
      let currentItemCollectionId = '';
      try {
        const settingsRes = await apiClient('/api/admin/settings/general');
        const settingsData = await settingsRes.json();
        if (settingsData?.success) {
          currentItemCollectionId = settingsData.data?.currentItemCollectionId || '';
        }
      } catch (settingsError) {
        console.warn('Unable to load current item collection setting:', settingsError);
      }

      const itemCollectionQuery = currentItemCollectionId
        ? `&itemCollectionId=${encodeURIComponent(currentItemCollectionId)}`
        : '';

      // Fetch clients, practitioners, shops, and services for dropdowns
      const [clientsRes, shopsRes, bodyZonesRes, piercingTypesRes] = await Promise.all([
        apiClient('/api/admin/clients?limit=1000'),
        apiClient('/api/admin/shops?limit=1000'),
        apiClient(`/api/admin/body-zones?limit=1000${itemCollectionQuery}`),
        apiClient(`/api/admin/piercing-types?limit=1000${itemCollectionQuery}`),
      ]);

      const [clientsData, shopsData, bodyZonesData, piercingTypesData] = await Promise.all([
        clientsRes.json(),
        shopsRes.json(),
        bodyZonesRes.json(),
        piercingTypesRes.json(),
      ]);

      if (clientsData.success) {
        setClients(
          clientsData.data.clients.map((c: any) => ({
            id: c.id,
            name: `${c.firstName} ${c.lastName} (${c.email})`,
          }))
        );
      }

      if (shopsData.success) {
        const nextShops = shopsData.data.shops.map((s: any) => ({
          id: s.id,
          name: `${s.name} - ${s.address.city}`,
        }));
        setShops(nextShops);
        const defaultShopId = resolveDefaultShopId(nextShops);
        if (defaultShopId) {
          setAppointment((prev) => {
            if (!prev || prev.shopId) return prev;
            return { ...prev, shopId: defaultShopId };
          });
        }
      }
      if (bodyZonesData.success) {
        setBodyZones(
          (bodyZonesData.data.bodyZones || []).map((z: any) => ({
            id: z.id,
            name: z.name,
          })).sort(byLocalizedName)
        );
      }
      if (piercingTypesData.success) {
        setPiercingTypes(
          (piercingTypesData.data.piercingTypes || []).map((pt: any) => ({
            id: pt.id,
            name: pt.name,
            bodyZoneId: pt.bodyZoneId,
            basePrice: Number(pt.basePrice || 0),
            estimatedDuration: Number(pt.estimatedDuration || 0),
          })).sort(byLocalizedName)
        );
      }
      await fetchServicesForShop(appointment?.shopId || undefined);
    } catch (error) {
      console.error('Fetch select options error:', error);
    }
  };

  const fetchServicesForShop = async (shopId?: string) => {
    try {
      const endpoint = shopId
        ? `/api/admin/services?limit=1000&shopId=${encodeURIComponent(shopId)}`
        : '/api/admin/services?limit=1000';
      const servicesRes = await apiClient(endpoint);
      const servicesData = await servicesRes.json();
      if (!servicesData.success) return;

      const nextServices = servicesData.data.services.map((s: any) => ({
        id: s.id,
        name: `${s.name} (${s.basePrice}€)`,
        price: s.basePrice,
        estimatedDuration: s.estimatedDuration,
        planningRecommendation: s.planningRecommendation || null,
        category: s.category,
      }));
      setServices(nextServices);

      if (
        appointment?.serviceId &&
        !nextServices.some((s: SelectOption) => s.id === appointment.serviceId)
      ) {
        setAppointment((prev) => (prev ? { ...prev, serviceId: '' } : prev));
      }
    } catch (error) {
      console.error('Fetch services for shop error:', error);
    }
  };

  const fetchShopPiercingOfferings = async (shopId?: string) => {
    if (!shopId) {
      setShopPiercingOfferings({});
      return;
    }
    try {
      const response = await apiClient(
        `/api/admin/shops/${encodeURIComponent(shopId)}/piercing-offerings`
      );
      const data = await response.json();
      if (!data?.success) {
        setShopPiercingOfferings({});
        return;
      }
      const byId: Record<string, ShopPiercingOffering> = {};
      for (const offering of data.data?.piercingOfferings || []) {
        byId[offering.id] = {
          id: offering.id,
          customPrice: offering.customPrice,
          customDurationMinutes: offering.customDurationMinutes,
          customMinorDurationMinutes: offering.customMinorDurationMinutes,
          basePrice: offering.basePrice,
          estimatedDuration: offering.estimatedDuration,
          isOffered: offering.isOffered,
          isAvailable: offering.isAvailable,
        };
      }
      setShopPiercingOfferings(byId);
    } catch (fetchError) {
      console.error('Fetch shop piercing offerings error:', fetchError);
      setShopPiercingOfferings({});
    }
  };

  const fetchPractitionersForSelection = async (shopId: string, serviceId: string) => {
    if (!shopId) {
      setPractitioners([]);
      if (appointment?.practitionerId) {
        setAppointment((prev) => (prev ? { ...prev, practitionerId: '' } : prev));
      }
      return;
    }
    try {
      const shopRes = await apiClient(
        `/api/admin/shops/${encodeURIComponent(shopId)}/practitioners`
      );
      const shopData = await shopRes.json();
      if (!shopData.success) {
        setPractitioners([]);
        return;
      }

      const shopPractitioners = (shopData.data?.practitioners || [])
        .filter((p: any) => p.isActive)
        .map((p: any) => ({
          id: p.practitionerId,
          name: `${p.practitionerFirstName} ${p.practitionerLastName}`.trim(),
        }));

      if (!serviceId) {
        setPractitioners(shopPractitioners);
        if (
          appointment?.practitionerId &&
          !shopPractitioners.some((p: SelectOption) => p.id === appointment.practitionerId)
        ) {
          setAppointment((prev) => (prev ? { ...prev, practitionerId: '' } : prev));
          setHasUnsavedChanges(true);
        }
        return;
      }

      const serviceRes = await apiClient(
        `/api/admin/services/${encodeURIComponent(serviceId)}/practitioners`
      );
      const serviceData = await serviceRes.json();
      if (!serviceData.success) {
        setPractitioners(shopPractitioners);
        return;
      }

      const allowedServicePractitionerIds = new Set(
        (serviceData.data?.practitioners || [])
          .filter((p: any) => p.isOffered && p.isAvailable)
          .map((p: any) => p.id)
      );
      const merged = shopPractitioners.filter((p: SelectOption) =>
        allowedServicePractitionerIds.has(p.id)
      );
      setPractitioners(merged);
      if (
        appointment?.practitionerId &&
        !merged.some((p: SelectOption) => p.id === appointment.practitionerId)
      ) {
        setAppointment((prev) => (prev ? { ...prev, practitionerId: '' } : prev));
        setHasUnsavedChanges(true);
      }
    } catch (fetchError) {
      console.error('Fetch filtered practitioners error:', fetchError);
      setPractitioners([]);
    }
  };
  const handleScheduledAtChange = (value: string) => {
    if (!appointment || !value) return;

    const newStart = new Date(value);
    if (Number.isNaN(newStart.getTime())) return;

    const oldStart = new Date(appointment.scheduledAt);
    const oldEnd = new Date(appointment.estimatedEndTime);

    // Conserver la durée actuelle du rendez-vous.
    // Fallback : durée du service, puis 60 minutes.
    let durationMs = 60 * 60 * 1000;

    if (!Number.isNaN(oldStart.getTime()) && !Number.isNaN(oldEnd.getTime()) && oldEnd > oldStart) {
      durationMs = oldEnd.getTime() - oldStart.getTime();
    } else {
      const selectedService = services.find((service) => service.id === appointment.serviceId);

      durationMs = Math.max(1, Number(selectedService?.estimatedDuration || 60)) * 60 * 1000;
    }

    const newEnd = new Date(newStart.getTime() + durationMs);

    setAppointment((prev) =>
      prev
        ? {
            ...prev,
            scheduledAt: newStart.toISOString(),
            estimatedEndTime: newEnd.toISOString(),
          }
        : prev
    );

    setHasUnsavedChanges(true);
  };
  const handleInputChange = (field: string, value: any) => {
    if (!appointment) return;

    const keys = field.split('.');
    setAppointment((prev) => {
      if (!prev) return prev;

      const newData = { ...prev };
      let current: any = newData;

      for (let i = 0; i < keys.length - 1; i++) {
        current[keys[i]] = { ...current[keys[i]] };
        current = current[keys[i]];
      }

      current[keys[keys.length - 1]] = value;
      return newData;
    });

    setHasUnsavedChanges(true);
  };

  const handleActChange = (index: number, field: keyof AppointmentAct, value: any) => {
    setAppointmentActs((prev) =>
      prev.map((act, i) => (i === index ? { ...act, [field]: value } : act))
    );
    setHasUnsavedChanges(true);
  };

  const handlePiercingFormChange = (actId: string, field: string, value: any) => {
    setPiercingRecords((prev) => ({
      ...prev,
      [actId]: {
        ...(prev[actId] ?? { form: defaultPiercingForm(), complicationsText: '', exists: false }),
        form: { ...(prev[actId]?.form ?? defaultPiercingForm()), [field]: value },
      },
    }));
    setHasUnsavedChanges(true);
  };

  const handlePiercingNested = (
    actId: string,
    section: 'placement' | 'jewelry' | 'technique' | 'aftercareProvided',
    field: string,
    value: any
  ) => {
    setPiercingRecords((prev) => {
      const current = prev[actId] ?? {
        form: defaultPiercingForm(),
        complicationsText: '',
        exists: false,
      };
      return {
        ...prev,
        [actId]: {
          ...current,
          form: {
            ...current.form,
            [section]: { ...(current.form[section] as any), [field]: value },
          },
        },
      };
    });
    setHasUnsavedChanges(true);
  };

  const handlePiercingComplicationsText = (actId: string, text: string) => {
    setPiercingRecords((prev) => ({
      ...prev,
      [actId]: {
        ...(prev[actId] ?? { form: defaultPiercingForm(), complicationsText: '', exists: false }),
        complicationsText: text,
      },
    }));
  };

  const savePiercingModal = async () => {
    if (!piercingModalActId) return;
    const prState = piercingRecords[piercingModalActId];
    if (!prState) return;
    try {
      setPiercingModalSaving(true);
      setPiercingModalError(null);
      const complications = (prState.complicationsText || '')
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);
      await upsertPiercingRecord(piercingModalActId, { ...prState.form, complications });
      setPiercingRecords((prev) => ({
        ...prev,
        [piercingModalActId]: {
          ...prev[piercingModalActId],
          exists: true,
          form: { ...prev[piercingModalActId].form, complications },
        },
      }));
      setPiercingModalActId(null);
    } catch (err: any) {
      setPiercingModalError(err.message || 'Erreur lors de la sauvegarde de la fiche piercing');
    } finally {
      setPiercingModalSaving(false);
    }
  };

  const saveTattooModal = async () => {
    if (!tattooModalActId) return;
    const trState = tattooRecords[tattooModalActId];
    if (!trState) return;
    try {
      setTattooModalSaving(true);
      setTattooModalError(null);
      await upsertTattooRecord(tattooModalActId, trState.form);
      setTattooRecords((prev) => ({
        ...prev,
        [tattooModalActId]: { ...prev[tattooModalActId], exists: true },
      }));
      setTattooModalActId(null);
    } catch (err: any) {
      setTattooModalError(err.message || 'Erreur lors de la sauvegarde de la fiche tatouage');
    } finally {
      setTattooModalSaving(false);
    }
  };

  const saveActs = async () => {
    if (!appointment?.id || appointmentActs.length === 0) return true;
    try {
      setActsSaving(true);
      setActsError(null);
      for (const act of appointmentActs) {
        const payload = {
          serviceId: act.serviceId,
          serviceCategory: act.serviceCategory || null,
          sequence: Number(act.sequence || 1),
          durationMinutes: Number(act.durationMinutes || 30),
          status: act.status,
          notes: act.notes || '',
          practitionerId: appointment.practitionerId || null,
          shopId: appointment.shopId,
          clientId: appointment.clientId || null,
          demandDetails: act.demandDetails || null,
          plannedStartAt: dateTimeLocalToIso(formatDateTimeForInput(act.plannedStartAt)),
          plannedEndAt: dateTimeLocalToIso(formatDateTimeForInput(act.plannedEndAt)),
        };
        const response = await apiClient(`/api/admin/acts/${encodeURIComponent(act.id)}`, {
          method: 'PUT',
          body: JSON.stringify(payload),
        });
        const data = await response.json();
        if (!data?.success) {
          setActsError(data?.message || 'Échec de la sauvegarde des actes.');
          return false;
        }
      }
      return true;
    } catch (saveError) {
      console.error('Save acts error:', saveError);
      setActsError('Erreur de connexion au serveur (sauvegarde des actes).');
      return false;
    } finally {
      setActsSaving(false);
    }
  };

  const updatePiercingItem = (index: number, patch: Partial<RequestedPiercingItem>) => {
    setRequestedPiercings((prev) =>
      prev.map((item, i) =>
        i === index
          ? {
              ...item,
              ...patch,
              piercingTypeId:
                patch.bodyZoneId !== undefined ? '' : (patch.piercingTypeId ?? item.piercingTypeId),
            }
          : item
      )
    );
    setHasUnsavedChanges(true);
  };

  const addPiercingItem = () => {
    setRequestedPiercings((prev) => [...prev, { bodyZoneId: '', piercingTypeId: '' }]);
    setHasUnsavedChanges(true);
  };

  const removePiercingItem = (index: number) => {
    setRequestedPiercings((prev) => {
      if (prev.length <= 1) return prev;
      return prev.filter((_, i) => i !== index);
    });
    setHasUnsavedChanges(true);
  };

  const handleServiceChange = (serviceId: string) => {
    handleInputChange('serviceId', serviceId);
    const selectedService = services.find((service) => service.id === serviceId);
    if (selectedService?.category === 'PIERCING') {
      handleInputChange('type', 'PIERCING');
    } else if (selectedService?.category === 'TATTOO') {
      handleInputChange('type', 'TATTOO_SESSION');
    } else if (selectedService?.category === 'CONSULTATION') {
      handleInputChange('type', 'CONSULTATION');
    } else if (selectedService?.category === 'REMOVAL') {
      handleInputChange('type', 'REMOVAL');
    } else if (selectedService?.category === 'AFTERCARE') {
      handleInputChange('type', 'AFTERCARE');
    }
  };

  const resetQuickClientForm = () => {
    setQuickClientData({
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      dateOfBirth: '',
      isMinor: null,
      consentGiven: false,
    });
    setQuickClientPhoneCountry('FR');
    setQuickClientPhoneDisplay('');
    setQuickClientPhoneE164(null);
    setQuickClientPhoneIsValid(false);
    setCreateClientError(null);
  };

  const handleCreateClientSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!quickClientData.firstName.trim()) {
      setCreateClientError('Le prénom est requis.');
      return;
    }
    if (quickClientPhoneDisplay.trim() && (!quickClientPhoneIsValid || !quickClientPhoneE164)) {
      setCreateClientError('Le numéro de téléphone est invalide pour le pays sélectionné.');
      return;
    }
    try {
      setCreatingClient(true);
      setCreateClientError(null);
      const response = await apiClient('/api/clients/quick-create', {
        method: 'POST',
        body: JSON.stringify({
          firstName: quickClientData.firstName.trim(),
          lastName: quickClientData.lastName.trim(),
          email: quickClientData.email.trim(),
          phone: quickClientPhoneE164 ?? '',
          dateOfBirth: quickClientData.dateOfBirth || undefined,
          isMinor: quickClientData.isMinor,
          consentGiven: quickClientData.consentGiven,
        }),
      });
      const data = await response.json();
      if (!data?.success || !data?.data?.client?.id) {
        setCreateClientError(data?.message || 'Impossible de créer le client.');
        return;
      }

      const newClientOption = {
        id: data.data.client.id,
        name: `${data.data.client.firstName} ${data.data.client.lastName} (${data.data.client.email || data.data.client.phone || 'sans contact'})`,
      };
      setClients((prev) => [newClientOption, ...prev]);
      handleInputChange('clientId', newClientOption.id);
      setShowCreateClientModal(false);
      resetQuickClientForm();
    } catch (createError) {
      console.error('Create client from appointment error:', createError);
      setCreateClientError('Erreur de connexion au serveur.');
    } finally {
      setCreatingClient(false);
    }
  };

  const handleSave = async () => {
    if (!appointment) return;

    try {
      setSaving(true);
      setError(null);
      setErrorDetails([]);

      // Helper function to clean datetime fields for API
      const cleanDateTimeForApi = (dateValue: any): string | null => {
        if (!dateValue) return null;

        // If it's already a valid ISO string, return it
        if (typeof dateValue === 'string' && dateValue.includes('T') && dateValue.includes('Z')) {
          return dateValue;
        }

        // Try to get the formatted input value first
        const inputValue = formatDateTimeForInput(dateValue);
        if (inputValue && inputValue.trim()) {
          // Convert datetime-local format to ISO string
          return new Date(inputValue + ':00').toISOString();
        }

        // Try to parse the original value
        try {
          const date = new Date(dateValue);
          if (!isNaN(date.getTime())) {
            return date.toISOString();
          }
        } catch (error) {
          console.warn('Failed to parse date:', dateValue, error);
        }

        return null;
      };

      const selectedPiercings = requestedPiercings
        .filter((item) => item.bodyZoneId && item.piercingTypeId)
        .map((item) => {
          const zone = bodyZones.find((z) => z.id === item.bodyZoneId);
          const piercingType = piercingTypes.find((pt) => pt.id === item.piercingTypeId);
          return {
            id: piercingType?.id || item.piercingTypeId,
            name: piercingType?.name || item.piercingTypeId,
            bodyZone: zone?.name || item.bodyZoneId,
          };
        });

      if (appointment.type === 'PIERCING' && selectedPiercings.length === 0) {
        setError('Veuillez renseigner au moins un type de piercing demandé.');
        setSaving(false);
        return;
      }

      const nonPiercingNotes = (appointment.notes || '')
        .replace(/^Piercings:\s.*?(?:\.\s*|$)/i, '')
        .trim();
      const piercingSummary =
        selectedPiercings.length > 0
          ? `Piercings: ${selectedPiercings.map((p) => `${p.bodyZone} - ${p.name}`).join(', ')}.`
          : '';
      const finalNotes = [piercingSummary, nonPiercingNotes].filter(Boolean).join(' ').trim();

      // Clean appointment data before sending
      const cleanAppointmentData = {
        id: appointment.id,
        clientId: appointment.clientId,
        practitionerId: appointment.practitionerId || null,
        reservedPractitionerId: appointment.practitionerId
          ? null
          : appointment.reservedPractitionerId || null,
        shopId: appointment.shopId,
        serviceId: appointment.serviceId,
        scheduledAt: cleanDateTimeForApi(appointment.scheduledAt),
        estimatedEndTime: cleanDateTimeForApi(appointment.estimatedEndTime),
        actualStartTime: cleanDateTimeForApi(appointment.actualStartTime),
        actualEndTime: cleanDateTimeForApi(appointment.actualEndTime),
        status: appointment.status,
        type: appointment.type,
        notes: finalNotes,
        preparation: appointment.preparation || {},
        pricing: {
          ...(appointment.pricing || {}),
          piercingTypes: selectedPiercings.length > 0 ? selectedPiercings : undefined,
          piercingType: selectedPiercings.length > 0 ? selectedPiercings[0] : undefined,
        },
        reminders: appointment.reminders || {},
        // Exclude createdAt, updatedAt, and extra client/shop fields
      };

      // Validate required fields before sending
      if (!cleanAppointmentData.scheduledAt) {
        setError(
          'Veuillez sélectionner une date et heure de rendez-vous. La valeur actuelle est invalide ou vide.'
        );
        setSaving(false);
        return;
      }

      if (!cleanAppointmentData.estimatedEndTime) {
        setError(
          'Veuillez sélectionner une heure de fin estimée. La valeur actuelle est invalide ou vide.'
        );
        setSaving(false);
        return;
      }

      // Additional validation: ensure scheduledAt is before estimatedEndTime
      const scheduledDate = new Date(cleanAppointmentData.scheduledAt);
      const endDate = new Date(cleanAppointmentData.estimatedEndTime);

      if (scheduledDate >= endDate) {
        setError("L'heure de fin estimée doit être postérieure à l'heure de début.");
        setSaving(false);
        return;
      }

      console.log('Sending clean appointment data:', cleanAppointmentData);

      const response = await apiClient(`/api/admin/appointments/${appointment.id}`, {
        method: 'PUT',
        body: JSON.stringify(cleanAppointmentData),
      });

      const data = await response.json();
      if (data.success) {
        const actsSaved = await saveActs();
        if (!actsSaved) {
          setSaving(false);
          return;
        }
        setHasUnsavedChanges(false);
        setError(null);
        setErrorDetails([]);
        if (appointment.id) {
          await fetchAppointmentActs(appointment.id);
        }
      } else {
        setError(
          localizeAppointmentApiMessage(data.message) || 'Échec de la sauvegarde du rendez-vous'
        );
        if (Array.isArray(data.errors) && data.errors.length > 0) {
          setErrorDetails(data.errors);
        }
      }
    } catch (error) {
      console.error('Save appointment error:', error);
      setError('Erreur de connexion au serveur');
    } finally {
      setSaving(false);
    }
  };

  const handleBackToList = () => {
    if (hasUnsavedChanges) {
      if (
        window.confirm(
          'Vous avez des modifications non sauvegardées. Êtes-vous sûr de vouloir quitter ?'
        )
      ) {
        navigate('/appointments');
      }
    } else {
      navigate('/appointments');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
        <span className="ml-2 text-gray-600">Chargement du rendez-vous...</span>
      </div>
    );
  }

  if (error || !appointment) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center">
            <AlertTriangle className="w-5 h-5 text-red-600 mr-2" />
            <span className="text-red-800 font-medium">{error || 'Rendez-vous non trouvé'}</span>
          </div>
        </div>
        <button onClick={() => navigate('/appointments')} className="mt-4 btn-secondary">
          Retour à la liste des rendez-vous
        </button>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center space-x-4">
          <button
            onClick={handleBackToList}
            className="flex items-center space-x-2 px-4 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors border border-gray-300 shadow-sm"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="hidden sm:inline">Retour à la liste</span>
          </button>

          <div>
            <h1 className="text-2xl font-display font-bold text-textPrimary flex items-center space-x-2">
              <span>Modifier Rendez-vous #{appointment.id.slice(-8)}</span>
              <HelpIcon
                docPath="07-gestion-rendez-vous#07-5-modification"
                tooltip="Documentation : Modification d'un rendez-vous"
              />
            </h1>
            {hasUnsavedChanges && (
              <p className="text-sm text-orange-600 bg-orange-50 px-2 py-1 rounded mt-1">
                Modifications non sauvegardées
              </p>
            )}
          </div>
        </div>

        <button
          onClick={handleSave}
          disabled={saving || actsSaving || !hasUnsavedChanges}
          className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
        >
          <Save className="w-4 h-4" />
          <span>{saving ? 'Sauvegarde...' : 'Sauvegarder'}</span>
        </button>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <p className="text-red-800 font-medium">{error}</p>
          {errorDetails.length > 0 && (
            <ul className="mt-2 text-sm text-red-700 list-disc list-inside space-y-1">
              {errorDetails.map((detail, i) => (
                <li key={i}>{detail}</li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* Form */}
      <div className="space-y-8">
        {/* Basic Information */}
        <div className="card">
          <div className="flex items-center space-x-3 mb-6">
            <div className="flex items-center justify-center w-10 h-10 bg-primary-100 rounded-lg">
              <Calendar className="w-5 h-5 text-primary-600" />
            </div>
            <h2 className="text-xl font-semibold text-textPrimary">Informations de Base</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div>
              <label className="block text-sm font-medium text-textPrimary mb-2">Shop *</label>
              <select
                value={appointment.shopId}
                onChange={(e) => {
                  const nextShopId = e.target.value;
                  setAppointment((prev) => {
                    if (!prev) return prev;
                    return {
                      ...prev,
                      shopId: nextShopId,
                      serviceId: '',
                      practitionerId: '',
                    };
                  });
                  setHasUnsavedChanges(true);
                  if (nextShopId) setCurrentShopId(nextShopId);
                }}
                className="input-field"
              >
                <option value="">Sélectionner un shop</option>
                {shops.map((shop) => (
                  <option key={shop.id} value={shop.id}>
                    {shop.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-textPrimary mb-2">Service *</label>
              <select
                value={appointment.serviceId}
                onChange={(e) => handleServiceChange(e.target.value)}
                className="input-field"
                disabled={!appointment.shopId}
              >
                <option value="">
                  {appointment.shopId ? 'Sélectionner un service' : 'Sélectionner d’abord un shop'}
                </option>
                {services.map((service) => (
                  <option key={service.id} value={service.id}>
                    {service.name}
                  </option>
                ))}
              </select>
              {(() => {
                const selectedService = services.find((s) => s.id === appointment.serviceId);
                const autoTypeLabel = getAutoTypeLabelFromCategory(selectedService?.category);
                if (!selectedService || !autoTypeLabel) return null;
                return (
                  <p className="mt-1 text-xs text-gray-500">
                    Type auto: <span className="font-medium text-gray-700">{autoTypeLabel}</span>
                  </p>
                );
              })()}
              {services.find((s) => s.id === appointment.serviceId)?.planningRecommendation && (
                <div
                  className="mt-2 text-xs text-blue-800 bg-blue-50 border border-blue-200 rounded p-2 [&_p]:my-1 [&_ul]:list-disc [&_ul]:pl-4 [&_ol]:list-decimal [&_ol]:pl-4"
                  dangerouslySetInnerHTML={{
                    __html:
                      services.find((s) => s.id === appointment.serviceId)
                        ?.planningRecommendation || '',
                  }}
                />
              )}
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-textPrimary">Client *</label>
                <div className="flex items-center gap-2">
                  {appointment.clientId && (
                    <button
                      type="button"
                      onClick={() => navigate(`/clients/${appointment.clientId}`)}
                      className="text-xs text-primary-700 hover:text-primary-900 hover:underline"
                    >
                      Voir le profil
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => {
                      resetQuickClientForm();
                      setShowCreateClientModal(true);
                    }}
                    className="text-xs px-2 py-1 border border-primary-300 text-primary-700 rounded hover:bg-primary-50"
                  >
                    Nouveau client
                  </button>
                </div>
              </div>
              <select
                value={appointment.clientId}
                onChange={(e) => handleInputChange('clientId', e.target.value)}
                className="input-field"
              >
                <option value="">Sélectionner un client</option>
                {clients.map((client) => (
                  <option key={client.id} value={client.id}>
                    {client.name}
                  </option>
                ))}
              </select>
              {appointment.clientIsMinor !== null
                && appointment.clientIsMinor !== undefined && (
                  <div
                    className={`mt-2 rounded-lg border p-3 text-sm ${
                      appointment.clientIsMinor
                        ? 'border-amber-200 bg-amber-50 text-amber-900'
                        : 'border-gray-200 bg-gray-50 text-gray-700'
                    }`}
                  >
                    <p className="font-semibold">
                      {appointment.clientIsMinor ? 'Client mineur' : 'Client majeur'}
                    </p>
                    {appointment.clientIsMinor && appointment.legalGuardian ? (
                      <div className="mt-2 border-t border-amber-200 pt-2 text-xs">
                        <p className="font-semibold uppercase tracking-wide text-amber-700">
                          Représentant légal
                        </p>
                        <p className="mt-1 font-medium">
                          {[appointment.legalGuardian.firstName, appointment.legalGuardian.lastName]
                            .filter(Boolean)
                            .join(' ') || '-'}
                        </p>
                        {appointment.legalGuardian.phone && (
                          <p>Téléphone : {appointment.legalGuardian.phone}</p>
                        )}
                        {appointment.legalGuardian.email && (
                          <p>Email : {appointment.legalGuardian.email}</p>
                        )}
                      </div>
                    ) : appointment.clientIsMinor ? (
                      <p className="mt-1 text-xs">Représentant légal non renseigné</p>
                    ) : null}
                  </div>
                )}
            </div>

            <div>
              <label className="block text-sm font-medium text-textPrimary mb-2">
                Date et heure *
              </label>
              <DateTimeInput
                value={formatDateTimeForInput(appointment.scheduledAt)}
                onChange={handleScheduledAtChange}
                className="input-field"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-textPrimary mb-2">
                {t('navigation.practitioners.title')} {appointment.type === 'PIERCING' ? '' : '*'}
              </label>
              <select
                value={appointment.practitionerId}
                onChange={(e) => handleInputChange('practitionerId', e.target.value)}
                className="input-field"
                disabled={!appointment.shopId || !appointment.serviceId}
              >
                <option value="">
                  {!appointment.shopId
                    ? 'Sélectionnez d’abord un shop'
                    : !appointment.serviceId
                      ? 'Sélectionnez d’abord un service'
                      : appointment.type === 'PIERCING'
                        ? 'Non assigné (assignation plus tard)'
                        : 'Sélectionner un artiste'}
                </option>
                {practitioners.map((practitioner) => (
                  <option key={practitioner.id} value={practitioner.id}>
                    {practitioner.name}
                  </option>
                ))}
              </select>
              {appointment.type === 'PIERCING' && !appointment.practitionerId && (
                <p className="mt-1 text-xs text-gray-500">
                  Rendez-vous non assigné. La capacité peut être réservée (via{' '}
                  <code>reservedPractitionerId</code>) et l’assignation finale se fait ici.
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-textPrimary mb-2">
                Heure de fin estimée *
              </label>
              <DateTimeInput
                value={formatDateTimeForInput(appointment.estimatedEndTime)}
                onChange={(value) =>
                  handleInputChange(
                    'estimatedEndTime',
                    value ? new Date(value).toISOString() : null
                  )
                }
                className="input-field"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-textPrimary mb-2">Statut *</label>
              <select
                value={appointment.status}
                onChange={(e) => handleInputChange('status', e.target.value)}
                className="input-field"
              >
                <option value="SCHEDULED">Programmé</option>
                <option value="CONFIRMED">Confirmé</option>
                <option value="CHECKED_IN">Arrivé</option>
                <option value="IN_PROGRESS">Pris en charge</option>
                <option value="COMPLETED">Terminé</option>
                <option value="CANCELLED">Annulé</option>
                <option value="NO_SHOW">Absent</option>
                <option value="RESCHEDULED">Reporté</option>
              </select>
            </div>

            {appointment.type === 'TATTOO_SESSION' && (
              <div className="md:col-span-2 lg:col-span-3 border border-purple-200 rounded-lg p-4 bg-purple-50">
                <p className="text-sm text-purple-800">
                  Projet tatouage: renseignez les détails (zone, style, dimensions, references) dans
                  les notes du rendez-vous.
                </p>
              </div>
            )}

            {appointment.type === 'PIERCING' && (
              <div className="md:col-span-2 lg:col-span-3 border border-gray-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <label className="block text-sm font-medium text-textPrimary">
                    Type(s) de piercing demandé(s) *
                  </label>
                  <button
                    type="button"
                    onClick={addPiercingItem}
                    className="px-3 py-1 text-xs bg-blue-50 text-blue-700 rounded border border-blue-200 hover:bg-blue-100"
                  >
                    Ajouter un piercing
                  </button>
                </div>
                <div className="space-y-3">
                  {requestedPiercings.map((item, index) => (
                    <div
                      key={`piercing-item-${index}`}
                      className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end"
                    >
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">Zone du corps</label>
                        <select
                          value={item.bodyZoneId}
                          onChange={(e) =>
                            updatePiercingItem(index, { bodyZoneId: e.target.value })
                          }
                          className="input-field"
                        >
                          <option value="">Sélectionner une zone</option>
                          {bodyZones.map((zone) => (
                            <option key={zone.id} value={zone.id}>
                              {zone.name}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">Type de piercing</label>
                        <select
                          value={item.piercingTypeId}
                          onChange={(e) =>
                            updatePiercingItem(index, { piercingTypeId: e.target.value })
                          }
                          className="input-field"
                          disabled={!item.bodyZoneId}
                        >
                          <option value="">Sélectionner un type</option>
                          {piercingTypes
                            .filter((pt) => pt.bodyZoneId === item.bodyZoneId)
                            .map((pt) => (
                              <option key={pt.id} value={pt.id}>
                                {pt.name}
                              </option>
                            ))}
                        </select>
                      </div>
                      <div>
                        <button
                          type="button"
                          onClick={() => removePiercingItem(index)}
                          disabled={requestedPiercings.length === 1}
                          className="px-3 py-2 text-xs bg-gray-100 text-gray-700 rounded border border-gray-300 hover:bg-gray-200 disabled:opacity-50"
                        >
                          Supprimer
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-6">
            <div>
              <label className="block text-sm font-medium text-textPrimary mb-2">
                Notes du rendez-vous
              </label>
              <textarea
                value={appointment.notes}
                onChange={(e) => handleInputChange('notes', e.target.value)}
                rows={3}
                className="input-field"
                placeholder="Notes internes sur le rendez-vous..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-textPrimary mb-2">
                Notes du client
              </label>
              {clientNoteSections.length > 0 ? (
                <div className="space-y-3">
                  {clientNoteSections.map((section: ClientNoteSection, index: number) => (
                    <div
                      key={`${section.title || 'note'}-${index}`}
                      className="bg-blue-50 p-3 rounded-lg"
                    >
                      {section.title && (
                        <p className="text-xs font-medium text-blue-900 mb-2">{section.title}</p>
                      )}
                      <div
                        className="text-gray-900 text-sm max-w-none
                          [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:my-2
                          [&_ol]:list-decimal [&_ol]:pl-6 [&_ol]:my-2
                          [&_li]:my-1 [&_p]:my-1"
                        dangerouslySetInnerHTML={{ __html: section.html }}
                      />
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-sm text-gray-500 bg-gray-50 border border-gray-200 rounded-lg p-3">
                  Aucune note client.
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Acts */}
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-textPrimary">Actes associés</h2>
            {actsLoading && <span className="text-xs text-gray-500">Chargement des actes...</span>}
          </div>
          {actsError && (
            <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
              {actsError}
            </div>
          )}
          {appointmentActs.length === 0 ? (
            <p className="text-sm text-gray-500">
              Aucun acte trouvé pour ce rendez-vous. Ils sont créés automatiquement selon les
              demandes.
            </p>
          ) : (
            <div className="space-y-4">
              {appointmentActs.map((act, index) => (
                <div key={act.id} className="border border-gray-200 rounded-lg p-4">
                  <p className="text-xs font-medium text-gray-500 mb-3">Acte #{index + 1}</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Service</label>
                      <select
                        value={act.serviceId}
                        onChange={(e) => handleActChange(index, 'serviceId', e.target.value)}
                        className="input-field"
                      >
                        {services.map((service) => (
                          <option key={service.id} value={service.id}>
                            {service.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Type service</label>
                      <div className="input-field bg-gray-50 text-gray-700">
                        {(() => {
                          const cat = effectiveActCategory(act, services);
                          return cat === 'PIERCING'
                            ? 'Piercing'
                            : cat === 'TATTOO'
                              ? 'Tatouage'
                              : cat || 'Non defini';
                        })()}
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Durée (minutes)</label>
                      <input
                        type="number"
                        min={1}
                        value={act.durationMinutes}
                        onChange={(e) =>
                          handleActChange(
                            index,
                            'durationMinutes',
                            parseInt(e.target.value, 10) || 1
                          )
                        }
                        className="input-field"
                      />
                    </div>
                    {(act.demandDetails?.bodyZone || act.demandDetails?.type) && (
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">Zone demandée</label>
                        <div className="input-field bg-gray-50 text-gray-600 text-sm">
                          {[act.demandDetails.bodyZone, act.demandDetails.type]
                            .filter(Boolean)
                            .join(' — ')}
                        </div>
                      </div>
                    )}
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Début acte</label>
                      <DateTimeInput
                        value={formatDateTimeForInput(act.plannedStartAt)}
                        onChange={(value) => handleActChange(index, 'plannedStartAt', value)}
                        className="input-field"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Fin acte</label>
                      <DateTimeInput
                        value={formatDateTimeForInput(act.plannedEndAt)}
                        onChange={(value) => handleActChange(index, 'plannedEndAt', value)}
                        className="input-field"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Statut acte</label>
                      <select
                        value={act.status}
                        onChange={(e) =>
                          handleActChange(
                            index,
                            'status',
                            e.target.value as AppointmentAct['status']
                          )
                        }
                        className="input-field"
                      >
                        <option value="PLANNED">Planifié</option>
                        <option value="IN_PROGRESS">En cours</option>
                        <option value="COMPLETED">Terminé</option>
                        <option value="CANCELLED">Annulé</option>
                      </select>
                    </div>
                    <div className="md:col-span-2 lg:col-span-3">
                      <label className="block text-xs text-gray-500 mb-1">Notes acte</label>
                      <input
                        type="text"
                        value={act.notes || ''}
                        onChange={(e) => handleActChange(index, 'notes', e.target.value)}
                        className="input-field"
                      />
                    </div>
                  </div>

                  {/* Satellite record button */}
                  {(effectiveActCategory(act, services) === 'PIERCING' ||
                    effectiveActCategory(act, services) === 'TATTOO') && (
                    <div className="mt-4 pt-3 border-t border-gray-100 flex items-center gap-3">
                      {effectiveActCategory(act, services) === 'PIERCING' && (
                        <>
                          <button
                            type="button"
                            onClick={() => {
                              setPiercingModalActId(act.id);
                              setPiercingModalError(null);
                            }}
                            className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-violet-700 bg-violet-50 border border-violet-200 rounded-lg hover:bg-violet-100 transition-colors"
                          >
                            <span>🩺</span>
                            <span>Fiche piercing</span>
                          </button>
                          {piercingRecords[act.id]?.exists ? (
                            <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">
                              ✓ Enregistrée
                            </span>
                          ) : (
                            <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">
                              À compléter
                            </span>
                          )}
                        </>
                      )}
                      {effectiveActCategory(act, services) === 'TATTOO' && (
                        <>
                          <button
                            type="button"
                            onClick={() => {
                              setTattooModalActId(act.id);
                              setTattooModalError(null);
                            }}
                            className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-orange-700 bg-orange-50 border border-orange-200 rounded-lg hover:bg-orange-100 transition-colors"
                          >
                            <span>🎨</span>
                            <span>Fiche tatouage</span>
                          </button>
                          {tattooRecords[act.id]?.exists ? (
                            <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">
                              ✓ Enregistrée
                            </span>
                          ) : (
                            <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">
                              À compléter
                            </span>
                          )}
                        </>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Timing */}
        <div className="card">
          <div className="flex items-center space-x-3 mb-6">
            <div className="flex items-center justify-center w-10 h-10 bg-blue-100 rounded-lg">
              <Clock className="w-5 h-5 text-blue-600" />
            </div>
            <h2 className="text-xl font-semibold text-textPrimary">Horaires Réels</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div>
              <label className="block text-sm font-medium text-textPrimary mb-2">
                Heure de début réelle
              </label>
              <DateTimeInput
                value={formatDateTimeForInput(appointment.actualStartTime)}
                onChange={(value) =>
                  handleInputChange('actualStartTime', value ? new Date(value).toISOString() : null)
                }
                className="input-field"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-textPrimary mb-2">
                Heure de fin réelle
              </label>
              <DateTimeInput
                value={formatDateTimeForInput(appointment.actualEndTime)}
                onChange={(value) =>
                  handleInputChange('actualEndTime', value ? new Date(value).toISOString() : null)
                }
                className="input-field"
              />
            </div>
          </div>
        </div>

        {/* Pricing */}
        <div className="card">
          <div className="flex items-center space-x-3 mb-6">
            <div className="flex items-center justify-center w-10 h-10 bg-green-100 rounded-lg">
              <FileText className="w-5 h-5 text-green-600" />
            </div>
            <h2 className="text-xl font-semibold text-textPrimary">Tarification</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-6">
            <div>
              <label className="block text-sm font-medium text-textPrimary mb-2">
                Prix de base (€)
              </label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={appointment.pricing.basePrice}
                onChange={(e) =>
                  handleInputChange('pricing.basePrice', parseFloat(e.target.value) || 0)
                }
                className="input-field"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-textPrimary mb-2">Acompte (€)</label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={appointment.pricing.deposit}
                onChange={(e) =>
                  handleInputChange('pricing.deposit', parseFloat(e.target.value) || 0)
                }
                className="input-field"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-textPrimary mb-2">Total (€)</label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={appointment.pricing.total}
                onChange={(e) =>
                  handleInputChange('pricing.total', parseFloat(e.target.value) || 0)
                }
                className="input-field"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-textPrimary mb-2">
                Méthode de paiement
              </label>
              <select
                value={appointment.pricing.paymentMethod}
                onChange={(e) => handleInputChange('pricing.paymentMethod', e.target.value)}
                className="input-field"
              >
                <option value="card">Carte bancaire</option>
                <option value="cash">Espèces</option>
                <option value="bank_transfer">Virement</option>
                <option value="digital_wallet">Portefeuille numérique</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-textPrimary mb-2">Devise</label>
              <select
                value={appointment.pricing.currency}
                onChange={(e) => handleInputChange('pricing.currency', e.target.value)}
                className="input-field"
              >
                <option value="EUR">EUR (€)</option>
                <option value="USD">USD ($)</option>
                <option value="GBP">GBP (£)</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Save Button (Mobile) */}
      <div className="mt-8 flex justify-end">
        <button
          onClick={handleSave}
          disabled={saving || actsSaving || !hasUnsavedChanges}
          className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
        >
          <Save className="w-4 h-4" />
          <span>{saving ? 'Sauvegarde...' : 'Sauvegarder'}</span>
        </button>
      </div>

      {/* Piercing Record Modal */}
      {piercingModalActId &&
        (() => {
          const actIndex = appointmentActs.findIndex((a) => a.id === piercingModalActId);
          const pr = piercingRecords[piercingModalActId] ?? {
            form: defaultPiercingForm(),
            complicationsText: '',
            exists: false,
          };
          return (
            <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4 overflow-y-auto">
              <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl my-4">
                <div className="flex items-center justify-between p-6 border-b border-gray-200">
                  <div>
                    <h3 className="text-lg font-semibold text-textPrimary">
                      Fiche piercing — Acte #{actIndex + 1}
                    </h3>
                    <p className="text-sm text-gray-500 mt-0.5">
                      Renseignez les détails cliniques du piercing réalisé
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setPiercingModalActId(null);
                      setPiercingModalError(null);
                    }}
                    className="text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    <span className="text-2xl leading-none">×</span>
                  </button>
                </div>

                <div className="p-6 space-y-6">
                  {piercingModalError && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
                      {piercingModalError}
                    </div>
                  )}

                  {/* Type & basics */}
                  <div>
                    <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">
                      Informations générales
                    </h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                      <div>
                        <label className="block text-sm font-medium text-textPrimary mb-1">
                          Zone du corps
                        </label>
                        <select
                          value={pr.form.bodyZoneId ?? ''}
                          onChange={(e) => {
                            handlePiercingFormChange(
                              piercingModalActId,
                              'bodyZoneId',
                              e.target.value || undefined
                            );
                            handlePiercingFormChange(
                              piercingModalActId,
                              'piercingTypeModelId',
                              undefined
                            );
                          }}
                          className="input-field"
                        >
                          <option value="">Sélectionner une zone</option>
                          {bodyZones.map((zone) => (
                            <option key={zone.id} value={zone.id}>
                              {zone.name}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-textPrimary mb-1">
                          Type de piercing (catalogue)
                        </label>
                        <select
                          value={pr.form.piercingTypeModelId ?? ''}
                          onChange={(e) =>
                            handlePiercingFormChange(
                              piercingModalActId,
                              'piercingTypeModelId',
                              e.target.value || undefined
                            )
                          }
                          className="input-field"
                          disabled={!pr.form.bodyZoneId}
                        >
                          <option value="">Sélectionner un type</option>
                          {piercingTypes
                            .filter((pt) => pt.bodyZoneId === pr.form.bodyZoneId)
                            .map((pt) => (
                              <option key={pt.id} value={pt.id}>
                                {pt.name}
                              </option>
                            ))}
                        </select>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-textPrimary mb-1">
                          Jauge aiguille
                        </label>
                        <input
                          type="text"
                          value={pr.form.needleGauge ?? ''}
                          onChange={(e) =>
                            handlePiercingFormChange(
                              piercingModalActId,
                              'needleGauge',
                              e.target.value || undefined
                            )
                          }
                          className="input-field"
                          placeholder="ex: 1.2mm (16G)"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-textPrimary mb-1">
                          Guérison estimée (semaines)
                        </label>
                        <input
                          type="number"
                          min={0}
                          value={pr.form.healingTime ?? ''}
                          onChange={(e) =>
                            handlePiercingFormChange(
                              piercingModalActId,
                              'healingTime',
                              e.target.value ? parseInt(e.target.value, 10) : undefined
                            )
                          }
                          className="input-field"
                          placeholder="ex: 8"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Placement */}
                  <div>
                    <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">
                      Placement
                    </h4>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-textPrimary mb-1">
                          Zone
                        </label>
                        <input
                          type="text"
                          value={pr.form.placement?.area ?? ''}
                          onChange={(e) =>
                            handlePiercingNested(
                              piercingModalActId,
                              'placement',
                              'area',
                              e.target.value
                            )
                          }
                          className="input-field"
                          placeholder="ex: Oreille, Nez..."
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-textPrimary mb-1">
                          Côté
                        </label>
                        <select
                          value={pr.form.placement?.side ?? ''}
                          onChange={(e) =>
                            handlePiercingNested(
                              piercingModalActId,
                              'placement',
                              'side',
                              e.target.value || undefined
                            )
                          }
                          className="input-field"
                        >
                          <option value="">Non précisé</option>
                          <option value="left">Gauche</option>
                          <option value="right">Droit</option>
                          <option value="center">Centre</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-textPrimary mb-1">
                          Précision
                        </label>
                        <input
                          type="text"
                          value={pr.form.placement?.specific ?? ''}
                          onChange={(e) =>
                            handlePiercingNested(
                              piercingModalActId,
                              'placement',
                              'specific',
                              e.target.value
                            )
                          }
                          className="input-field"
                          placeholder="ex: Hélix, Tragus..."
                        />
                      </div>
                    </div>
                  </div>

                  {/* Bijou */}
                  <div>
                    <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">
                      Bijou posé
                    </h4>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-textPrimary mb-1">
                          Type
                        </label>
                        <input
                          type="text"
                          value={pr.form.jewelry?.type ?? ''}
                          onChange={(e) =>
                            handlePiercingNested(
                              piercingModalActId,
                              'jewelry',
                              'type',
                              e.target.value
                            )
                          }
                          className="input-field"
                          placeholder="ex: Anneau segment, Barbell..."
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-textPrimary mb-1">
                          Matière
                        </label>
                        <input
                          type="text"
                          value={pr.form.jewelry?.material ?? ''}
                          onChange={(e) =>
                            handlePiercingNested(
                              piercingModalActId,
                              'jewelry',
                              'material',
                              e.target.value
                            )
                          }
                          className="input-field"
                          placeholder="ex: Titane implant grade"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-textPrimary mb-1">
                          Jauge bijou
                        </label>
                        <input
                          type="text"
                          value={pr.form.jewelry?.gauge ?? ''}
                          onChange={(e) =>
                            handlePiercingNested(
                              piercingModalActId,
                              'jewelry',
                              'gauge',
                              e.target.value
                            )
                          }
                          className="input-field"
                          placeholder="ex: 1.2mm"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Technique */}
                  <div>
                    <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">
                      Technique
                    </h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-textPrimary mb-1">
                          Méthode
                        </label>
                        <input
                          type="text"
                          value={pr.form.technique?.method ?? ''}
                          onChange={(e) =>
                            handlePiercingNested(
                              piercingModalActId,
                              'technique',
                              'method',
                              e.target.value
                            )
                          }
                          className="input-field"
                          placeholder="ex: Aiguille standard, Dermal punch..."
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-textPrimary mb-1">
                          Outils
                        </label>
                        <input
                          type="text"
                          value={pr.form.technique?.tools ?? ''}
                          onChange={(e) =>
                            handlePiercingNested(
                              piercingModalActId,
                              'technique',
                              'tools',
                              e.target.value
                            )
                          }
                          className="input-field"
                          placeholder="ex: Pince, cathéter..."
                        />
                      </div>
                    </div>
                  </div>

                  {/* Aftercare */}
                  <div>
                    <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">
                      Soins fournis
                    </h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-textPrimary mb-1">
                          Produits remis
                        </label>
                        <textarea
                          rows={2}
                          value={pr.form.aftercareProvided?.products ?? ''}
                          onChange={(e) =>
                            handlePiercingNested(
                              piercingModalActId,
                              'aftercareProvided',
                              'products',
                              e.target.value
                            )
                          }
                          className="input-field"
                          placeholder="ex: Spray NeilMed, Notice de soin..."
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-textPrimary mb-1">
                          Instructions données
                        </label>
                        <textarea
                          rows={2}
                          value={pr.form.aftercareProvided?.instructions ?? ''}
                          onChange={(e) =>
                            handlePiercingNested(
                              piercingModalActId,
                              'aftercareProvided',
                              'instructions',
                              e.target.value
                            )
                          }
                          className="input-field"
                          placeholder="ex: Nettoyer 2x/jour avec spray salin..."
                        />
                      </div>
                    </div>
                  </div>

                  {/* Complications & healed */}
                  <div>
                    <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">
                      Suivi
                    </h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-textPrimary mb-1">
                          Complications observées
                        </label>
                        <input
                          type="text"
                          value={pr.complicationsText}
                          onChange={(e) =>
                            handlePiercingComplicationsText(piercingModalActId, e.target.value)
                          }
                          className="input-field"
                          placeholder="Séparer par des virgules — laisser vide si aucune"
                        />
                      </div>
                      <div className="flex items-center gap-3 pt-6">
                        <input
                          type="checkbox"
                          id="piercingModalIsHealed"
                          checked={pr.form.isHealed ?? false}
                          onChange={(e) =>
                            handlePiercingFormChange(
                              piercingModalActId,
                              'isHealed',
                              e.target.checked
                            )
                          }
                          className="w-4 h-4 text-violet-600 rounded"
                        />
                        <label
                          htmlFor="piercingModalIsHealed"
                          className="text-sm font-medium text-textPrimary"
                        >
                          Piercing guéri
                        </label>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-xl">
                  <button
                    type="button"
                    onClick={() => {
                      setPiercingModalActId(null);
                      setPiercingModalError(null);
                    }}
                    className="btn-secondary"
                    disabled={piercingModalSaving}
                  >
                    Annuler
                  </button>
                  <button
                    type="button"
                    onClick={savePiercingModal}
                    disabled={piercingModalSaving}
                    className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {piercingModalSaving ? 'Enregistrement...' : 'Sauvegarder la fiche'}
                  </button>
                </div>
              </div>
            </div>
          );
        })()}

      {/* Tattoo Record Modal */}
      {tattooModalActId &&
        (() => {
          const tr = tattooRecords[tattooModalActId] ?? {
            form: defaultTattooForm(),
            exists: false,
          };
          const setTattooField = (field: keyof UpsertTattooRecordData, value: any) =>
            setTattooRecords((prev) => ({
              ...prev,
              [tattooModalActId]: {
                ...(prev[tattooModalActId] ?? { form: defaultTattooForm(), exists: false }),
                form: { ...(prev[tattooModalActId]?.form ?? defaultTattooForm()), [field]: value },
              },
            }));
          return (
            <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
              <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col">
                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
                  <h3 className="text-lg font-semibold text-textPrimary">🎨 Fiche tatouage</h3>
                  <button
                    type="button"
                    onClick={() => {
                      setTattooModalActId(null);
                      setTattooModalError(null);
                    }}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    ✕
                  </button>
                </div>
                {tattooModalError && (
                  <div className="mx-6 mt-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                    {tattooModalError}
                  </div>
                )}
                <div className="flex-1 overflow-y-auto px-6 py-4 space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-textPrimary mb-1">
                      Zone du corps
                    </label>
                    <select
                      value={tr.form.bodyZoneId ?? ''}
                      onChange={(e) => setTattooField('bodyZoneId', e.target.value || undefined)}
                      className="input-field"
                    >
                      <option value="">Sélectionner une zone</option>
                      {bodyZones.map((zone) => (
                        <option key={zone.id} value={zone.id}>
                          {zone.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-textPrimary mb-1">
                        Style
                      </label>
                      <select
                        value={tr.form.style ?? ''}
                        onChange={(e) =>
                          setTattooField('style', (e.target.value as TattooStyle) || undefined)
                        }
                        className="input-field"
                      >
                        <option value="">Non précisé</option>
                        <option value="TRADITIONAL">Traditionnel</option>
                        <option value="REALISM">Réalisme</option>
                        <option value="WATERCOLOR">Aquarelle</option>
                        <option value="GEOMETRIC">Géométrique</option>
                        <option value="MINIMALIST">Minimaliste</option>
                        <option value="BLACKWORK">Blackwork</option>
                        <option value="DOTWORK">Dotwork</option>
                        <option value="TRIBAL">Tribal</option>
                        <option value="JAPANESE">Japonais</option>
                        <option value="BIOMECHANICAL">Biomécanique</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-textPrimary mb-1">
                        Couleurs
                      </label>
                      <select
                        value={tr.form.colorType ?? ''}
                        onChange={(e) =>
                          setTattooField('colorType', (e.target.value as ColorType) || undefined)
                        }
                        className="input-field"
                      >
                        <option value="">Non précisé</option>
                        <option value="BLACK_GREY">Noir &amp; Gris</option>
                        <option value="COLOR">Couleur</option>
                        <option value="MIXED">Mixte</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-textPrimary mb-1">
                        Complexité
                      </label>
                      <select
                        value={tr.form.complexity ?? ''}
                        onChange={(e) =>
                          setTattooField(
                            'complexity',
                            (e.target.value as ComplexityLevel) || undefined
                          )
                        }
                        className="input-field"
                      >
                        <option value="">Non précisé</option>
                        <option value="SIMPLE">Simple</option>
                        <option value="MODERATE">Modérée</option>
                        <option value="COMPLEX">Complexe</option>
                        <option value="MASTERPIECE">Chef-d'œuvre</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-textPrimary mb-1">
                        Emplacement précis
                      </label>
                      <input
                        type="text"
                        value={tr.form.placement?.specific ?? ''}
                        onChange={(e) =>
                          setTattooField('placement', {
                            ...(tr.form.placement ?? {}),
                            specific: e.target.value,
                          })
                        }
                        className="input-field"
                        placeholder="ex: avant-bras intérieur"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-textPrimary mb-1">
                        Largeur (cm)
                      </label>
                      <input
                        type="number"
                        min={0}
                        step={0.5}
                        value={tr.form.size?.width ?? ''}
                        onChange={(e) =>
                          setTattooField('size', {
                            ...(tr.form.size ?? {}),
                            width: parseFloat(e.target.value) || undefined,
                          })
                        }
                        className="input-field"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-textPrimary mb-1">
                        Hauteur (cm)
                      </label>
                      <input
                        type="number"
                        min={0}
                        step={0.5}
                        value={tr.form.size?.height ?? ''}
                        onChange={(e) =>
                          setTattooField('size', {
                            ...(tr.form.size ?? {}),
                            height: parseFloat(e.target.value) || undefined,
                          })
                        }
                        className="input-field"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-textPrimary mb-1">
                      Notes de séance
                    </label>
                    <textarea
                      rows={4}
                      value={tr.form.sessionNotes ?? ''}
                      onChange={(e) => setTattooField('sessionNotes', e.target.value)}
                      className="input-field"
                      placeholder="Zones travaillées, détails techniques, observations..."
                    />
                  </div>
                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      id="tattooModalIsCompleted"
                      checked={tr.form.isCompleted ?? false}
                      onChange={(e) => setTattooField('isCompleted', e.target.checked)}
                      className="w-4 h-4 text-orange-600 rounded"
                    />
                    <label
                      htmlFor="tattooModalIsCompleted"
                      className="text-sm font-medium text-textPrimary"
                    >
                      Séance terminée / tatouage finalisé
                    </label>
                  </div>
                </div>
                <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-xl">
                  <button
                    type="button"
                    onClick={() => {
                      setTattooModalActId(null);
                      setTattooModalError(null);
                    }}
                    className="btn-secondary"
                    disabled={tattooModalSaving}
                  >
                    Annuler
                  </button>
                  <button
                    type="button"
                    onClick={saveTattooModal}
                    disabled={tattooModalSaving}
                    className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {tattooModalSaving ? 'Enregistrement...' : 'Sauvegarder la fiche'}
                  </button>
                </div>
              </div>
            </div>
          );
        })()}

      {showCreateClientModal && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-xl p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-textPrimary">Créer un client</h3>
              <button
                type="button"
                onClick={() => setShowCreateClientModal(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                Fermer
              </button>
            </div>

            {createClientError && (
              <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                {createClientError}
              </div>
            )}

            <form onSubmit={handleCreateClientSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-textPrimary mb-1">
                    Prénom *
                  </label>
                  <input
                    type="text"
                    className="input-field"
                    value={quickClientData.firstName}
                    onChange={(e) =>
                      setQuickClientData((prev) => ({ ...prev, firstName: e.target.value }))
                    }
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-textPrimary mb-1">Nom (optionnel)</label>
                  <input
                    type="text"
                    className="input-field"
                    value={quickClientData.lastName}
                    onChange={(e) =>
                      setQuickClientData((prev) => ({ ...prev, lastName: e.target.value }))
                    }
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-textPrimary mb-1">Email</label>
                  <input
                    type="email"
                    className="input-field"
                    value={quickClientData.email}
                    onChange={(e) =>
                      setQuickClientData((prev) => ({ ...prev, email: e.target.value }))
                    }
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-textPrimary mb-1">
                    Téléphone
                  </label>
                  <InternationalPhoneInput
                    value={quickClientPhoneDisplay}
                    country={quickClientPhoneCountry}
                    onChange={(result) => {
                      setQuickClientPhoneDisplay(result.displayValue);
                      setQuickClientPhoneCountry(result.country);
                      setQuickClientPhoneE164(result.e164Value);
                      setQuickClientPhoneIsValid(result.isValid);
                      setQuickClientData((prev) => ({ ...prev, phone: result.displayValue }));

                      if (result.isValid) {
                        setCreateClientError((prev) =>
                          prev?.startsWith('Le numéro de téléphone est invalide') ? null : prev
                        );
                      }
                    }}
                    selectClassName="input-field"
                    inputClassName="input-field"
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-textPrimary mb-1">
                    Date de naissance (optionnel)
                  </label>
                  <DateInput
                    value={quickClientData.dateOfBirth}
                    disabled={quickClientData.isMinor === true && !quickClientData.dateOfBirth}
                    onChange={(value) =>
                      setQuickClientData((prev) => ({
                        ...prev,
                        dateOfBirth: value,
                        isMinor: value ? isMinor(value) : null,
                      }))
                    }
                  />
                </div>
                <label className="flex items-center gap-2 self-end pb-3 text-sm text-textPrimary">
                  <input
                    type="checkbox"
                    checked={quickClientData.isMinor === true}
                    disabled={Boolean(quickClientData.dateOfBirth)}
                    onChange={(e) =>
                      setQuickClientData((prev) => ({
                        ...prev,
                        isMinor: e.target.checked ? true : null,
                      }))
                    }
                    className="h-4 w-4 rounded border-gray-300 text-primary-600"
                  />
                  Mineur (optionnel)
                </label>
              </div>
              <label className="flex items-start gap-2 text-sm text-textPrimary">
                <input
                  type="checkbox"
                  checked={quickClientData.consentGiven}
                  onChange={(e) =>
                    setQuickClientData((prev) => ({ ...prev, consentGiven: e.target.checked }))
                  }
                  className="mt-1"
                />
                <span>
                  Consentement du client (optionnel).
                </span>
              </label>
              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setShowCreateClientModal(false)}
                  className="btn-secondary"
                  disabled={creatingClient}
                >
                  Annuler
                </button>
                <button type="submit" className="btn-primary" disabled={creatingClient}>
                  {creatingClient ? 'Création...' : 'Créer le client'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
